import React from 'react';
import { QualificationLevel, UserRole } from '../../../user/user.type';
import TagSelectorUi from '../../../ui/tag-selector.ui';
import TagInputUi from '../../../ui/tag-input.ui';
import SelectUi from '../../../ui/select.ui';
import InputUi, { InputType } from '../../../ui/input.ui';
import DeleteIcon from '../../../ui/delete-icon.ui';

import styles from './onboarding-step-skills.module.css';

const qualificationLevelOptions = Object.values(QualificationLevel);
const qualificationLevelOptionsUi = qualificationLevelOptions.map((level) => ({
  value: level,
  title: level,
}));

type OnboardingStepSkillsUiProps = {
  role: UserRole | '';
  engineerSkillsTags?: string[];
  skills?: string[];
  skillsLoading?: boolean;
  skillsFetchError?: string | null;
  skillsError: string | undefined;
  engineerSkillsMaxSelection?: number;
  founderSkillsTags?: string[];
  founderSkillsMaxSelection?: number;
  projectCategoriesTags?: string[];
  notableCompanies?: string[];
  notableCompaniesError?: string;
  qualificationLevel: string;
  educationFormErrors?: {
    level: boolean;
    title: boolean;
    institution: boolean;
  };
  qualificationTitle: string;
  qualificationInstitution: string;
  degrees?: string[];
  experience?: number;
  isStudent?: boolean;
  degreesError?: string;
  onAddDegrees: () => void | Promise<void>;
  onRemoveDegrees: (degree: string) => void;
  onUpdateField: (field: string, action: 'add' | 'remove', value: string) => void;
  onUpdateBooleanField: () => void;
}

const OnboardingStepSkillsUi = (props: OnboardingStepSkillsUiProps) => {
  const {
    role,
    engineerSkillsTags,
    skills,
    skillsLoading,
    skillsFetchError,
    skillsError,
    engineerSkillsMaxSelection,
    founderSkillsTags,
    founderSkillsMaxSelection,
    notableCompanies,
    notableCompaniesError,
    qualificationLevel,
    qualificationTitle,
    qualificationInstitution,
    educationFormErrors,
    degrees,
    degreesError,
    experience,
    isStudent,
    onAddDegrees,
    onRemoveDegrees,
    onUpdateField,
    onUpdateBooleanField,
  } = props;

  return (
    <>
      <div className={styles.titleWrapper}>
        <div className={styles.title}>Select up to 3 skills</div>
      </div>

      {role === UserRole.SoftwareEngineer ? (
        <TagSelectorUi
          tags={engineerSkillsTags || []}
          selectedTags={skills || []}
          loading={skillsLoading}
          error={skillsError}
          maxSelection={engineerSkillsMaxSelection}
          allowCustom
          placeholder="Add a Custom Skill (press Enter to add)"
          onAddTag={(skill) => onUpdateField('skills', 'add', skill)}
          onRemoveTag={(skill) => onUpdateField('skills', 'remove', skill)}
        />
      ) : (
        <TagSelectorUi
          tags={founderSkillsTags || []}
          selectedTags={skills || []}
          maxSelection={founderSkillsMaxSelection}
          allowCustom
          error={skillsError}
          placeholder="Add a Custom Skill..."
          onAddTag={(skill) => onUpdateField('skills', 'add', skill)}
          onRemoveTag={(skill) => onUpdateField('skills', 'remove', skill)}
        />
      )}

      {skillsFetchError && (
        <div className={styles.error}>{skillsFetchError}</div>
      )}

      <TagInputUi
        values={notableCompanies || []}
        onAdd={(company) => onUpdateField('notableCompanies', 'add', company)}
        onRemove={(company) => onUpdateField('notableCompanies', 'remove', company)}
        labelText="Any Notable Companies You've Worked For?"
        placeholder="Enter company name (press Enter to add)"
      />
      {notableCompaniesError && (
        <div className={styles.error}>{notableCompaniesError}</div>
      )}

      <div className={styles.experienceWrapper}>
        <InputUi
          type={InputType.number}
          id="yearsOfExperience"
          value={experience?.toString() || ''}
          min={0}
          labelText="Years of Experience"
          onChange={(val) => onUpdateField('yearsOfExperience', 'add', val)}
        />
        <InputUi
          type={InputType.checkbox}
          id="isStudent"
          value={isStudent ? 'true' : 'false'}
          labelText={'Are you a student?'}
          className={'checkbox'}
          onChange={onUpdateBooleanField}
        />
      </div>

      <div className={styles.educationTitle}>Education</div>
      <div className={styles.educationForm}>
        <SelectUi
          onChange={(level) => onUpdateField('qualificationLevel', 'add', level)}
          options={qualificationLevelOptionsUi}
          value={qualificationLevel}
          placeholder="Qualification Level"
          error={educationFormErrors?.level}
          className={educationFormErrors?.level ? styles.inputError : ''}
        />

        <InputUi
          id={'qualificationTitle'}
          type={InputType.text}
          value={qualificationTitle}
          error={educationFormErrors?.title}
          placeholder={'Qualification Title (e.g. Product Design)'}
          onChange={(val) => onUpdateField('qualificationTitle', 'add', val)}
        />

        <InputUi
          id={'institution'}
          type={InputType.text}
          value={qualificationInstitution}
          error={educationFormErrors?.institution}
          placeholder={'University or Institution'}
          onChange={(val) => onUpdateField('qualificationInstitution', 'add', val)}
        />
      </div>

      <button
        type="button"
        onClick={onAddDegrees}
        className={styles.addDegreeButton}
      >
        Add Degree
      </button>

      {degrees && degrees.length > 0 && (
        <div className={styles.degreesDisplay}>
          <div className={styles.degreesList}>
            {degrees.map((degree, index) => (
              <div key={index} className={styles.degreeItem}>
                <div className={styles.degreeText}>{degree}</div>
                <DeleteIcon onClick={() => onRemoveDegrees(degree)} />
              </div>
            ))}
          </div>
        </div>
      )}

      {degreesError && <div className={styles.error}>{degreesError}</div>}
    </>
  );
};

export default OnboardingStepSkillsUi;
